import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  LangItem,
  AppLang,
  SupportedLang,
  LABEL_KEY_MAP,
  LABEL_FLAG_MAP,
} from '@core/models/language.model';

const STORAGE_KEY = 'language';

@Injectable({ providedIn: 'root' })
export class TranslationService {
  #translate = inject(TranslateService);

  readonly languages: readonly LangItem<SupportedLang>[] = environment.supportedLanguages.map(
    (code) => ({
      code,
      labelKey: LABEL_KEY_MAP[code],
      flag: LABEL_FLAG_MAP[code],
    }),
  ) as readonly LangItem<SupportedLang>[];

  #currentLangSubject = new BehaviorSubject<string>(this.getStoredOrDefault());
  readonly currentLang$: Observable<string> = this.#currentLangSubject.asObservable();

  async init(): Promise<void> {
    const supported = environment.supportedLanguages ?? this.languages.map((l) => l.code);

    const supportedArray: string[] = Array.isArray(supported)
      ? [...supported]
      : Array.from(supported);
    this.#translate.addLangs(supportedArray);
    const defaultLang = environment.defaultLanguage ?? this.languages[0].code;
    const lang = this.#currentLangSubject.value;

    try {
      await firstValueFrom(this.#translate.use(lang));
      this.#currentLangSubject.next(lang);
    } catch (_err) {
      try {
        await firstValueFrom(this.#translate.use(defaultLang));
        this.#currentLangSubject.next(defaultLang);
      } catch {
        this.#currentLangSubject.next(defaultLang);
      }
    }
  }

  normalize(lang: string | null | undefined): string {
    if (!lang) return environment.defaultLanguage ?? AppLang.ES;
    const lower = lang.toLowerCase();
    if (lower === 'es') return AppLang.ES;
    if (lower === 'en') return AppLang.EN;
    return lang;
  }

  private getStoredOrDefault(): string {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return this.normalize(stored);
    } catch {
      console.warn('Error accessing localStorage for language preference');
    }
    // fallback: browser lang o default
    const browser = this.#translate.getBrowserLang();
    return this.normalize(browser ?? environment.defaultLanguage);
  }

  setLanguage(lang: string): void {
    const normalized = this.normalize(lang);
    this.useLanguage(normalized);
  }

  toggleLanguage(): void {
    const current = this.#currentLangSubject.value;
    const next = current === AppLang.ES ? AppLang.EN : AppLang.ES;
    this.setLanguage(next);
  }

  getCurrentLanguage(): string {
    return this.#currentLangSubject.value;
  }

  translate(key: string): string {
    return this.#translate.instant(key);
  }

  private useLanguage(lang: string) {
    this.#translate.use(lang).subscribe({
      next: () => {
        this.#currentLangSubject.next(lang);
        this.persistLanguage(lang);
      },
      error: (err) => {
        console.warn('Error loading translations', lang, err);
        const fallback = environment.defaultLanguage ?? AppLang.ES;
        if (lang !== fallback) {
          this.#translate.use(fallback).subscribe(() => this.#currentLangSubject.next(fallback));
        }
      },
    });
  }

  private persistLanguage(lang: string) {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        console.warn('Error storing translations', lang);
      }
    }
  }
}
